import PrintSettingsCache from "../../../components/printSettings/cache/PrintSettingsCache";
import { EventEditionDetails } from "../../types";
import { BusinessUnit, ReceiptBuilder, ReceiptBuilderProps } from "@reedexpo/mercury-badge";
import { PrintMethods } from "../../enums";
import print from "print-js";
import { useSelector } from "react-redux";
import { eventEditionSelector } from "../../../redux/eventEdition/selectors/eventEdition.selectors";
import { PrintSettingsCacheValue } from "../../../components/printSettings/types";
import { BadgeDownloadOptions } from "../../../components/search/details/download/types";
import PrintService from "../../../services/PrintService";
import { SNACKBAR_VARIANTS } from "../../constants";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import getReceiptDateTime from "../../utils/getReceiptDateTime";
import { PrintReceipt, PrintReceiptParams } from "./receipts.types";
import { useReceiptPropGeneration } from "./useReceiptPropGeneration/useReceiptPropGeneration";
import EventHelper from "../../network/helpers/event/EventHelper";
import { useCmsImages } from "../../../components/entry/hooks/useCmsImages/useCmsImages";
import { fonts, ZPLBadge } from "../../badges/builder/types";
import store from "../../../redux/store";
import getCountryISO2 from "country-iso-3-to-2";
import PublicApiService from "../../../services/PublicApiService";
import { productsSelector } from "../../../redux/magento/selectors/magento.selectors";
import { isProductWithTax } from "../../../components/createBadge/utils/utils";

const useReceiptPrint = () => {
  const eventEditionDetails = useSelector(eventEditionSelector).eventEdition as EventEditionDetails;
  const { enqueueSnackbar } = useSnackbar();
  const { i18n, t } = useTranslation("common");
  const { getPropsForUpgrades, getPropsForPartialPayments } = useReceiptPropGeneration();
  const { getReceiptLogoUrl } = useCmsImages();
  const products = useSelector(productsSelector);

  const printWithServer = async (receipt: Blob, cachedOptions: PrintSettingsCacheValue, options: BadgeDownloadOptions = {}) => {
    try {
      const isServerConnected = await PrintService.isPrintServerConnected();
      if (!isServerConnected) {
        await PrintService.setupPdfWS({ useCache: true });
      }
      PrintService.print(receipt, { selectedPrinter: cachedOptions.settingsValues.selectedPrinter });
      if (!options.hideToastMessage) {
        enqueueSnackbar(<b>{t("common.badgeSentToPrinter")}</b>, SNACKBAR_VARIANTS.success);
      }
    } catch (e) {
      enqueueSnackbar(<b>{t("errors.unableToConnectToPrintServer")}</b>, SNACKBAR_VARIANTS.error);
    }
  };

  const getReceiptBlob = async (receiptParams: PrintReceiptParams) => {
    const { badge, options, price } = receiptParams;
    const currency = EventHelper.eventEdition!.defaultCurrencyCode;
    const upgradePrice = { currency, value: price };
    const customBadgeUpgradePrice = options?.customBadgeUpgradePrice;
    const logoUrl = await getReceiptLogoUrl(i18n.language);
    const props = options?.partialPaymentsData
      ? getPropsForPartialPayments(options)
      : getPropsForUpgrades({ ...receiptParams, upgradePrice, customBadgeUpgradePrice });

    const orderId = props.orders?.[0] || receiptParams.badge?.orderId || receiptParams.orderId || "";
    const blob = await ReceiptBuilder.getBlob({
      ...props,
      isInclusiveTax: EventHelper.isEventMAPIT(),
      country: getCountryISO2(store.getState().eventEdition.cmsEventDetails?.siteDetails.address.country || ""),
      currency: EventHelper.eventEdition?.defaultCurrencyCode || "",
      businessUnit: EventHelper.eventEdition!.businessUnitGbsCode as BusinessUnit,
      locale: i18n.language,
      logo: logoUrl,
      date: getReceiptDateTime(),
      companyName: badge.organisation.displayName,
      fonts,
      fontsCulture: i18n.language
    } as ReceiptBuilderProps);

    return { blob, orderId };
  };

  const directReceiptPrint = (receiptBlob: Blob) => {
    const receiptUrl = URL.createObjectURL(receiptBlob);
    print(receiptUrl);
    URL.revokeObjectURL(receiptUrl);
  };

  const printWithServerWithOptions = async (receiptBlob: Blob) => {
    const cachedOptions = PrintSettingsCache.get();

    await printWithServer(receiptBlob, cachedOptions);
    if (EventHelper.isAustraliaBu()) {
      await PrintService.printZPL((receiptBlob as unknown as ZPLBadge)?.z64, {
        selectedPrinter: cachedOptions.settingsValues.selectedPrinter
      });
      return;
    }
    if (EventHelper.isFranceBu() || EventHelper.isUkBu()) {
      await printWithServer(receiptBlob, cachedOptions);
    }
  };

  const getInclusiveTax = (printReceiptParams: PrintReceipt) => {
    const productSku =
      printReceiptParams.newBadgeProduct ?? printReceiptParams.badge.productCode ?? printReceiptParams.badgeUpgradeOptions?.newProductCode;

    if (!productSku) {
      return EventHelper.isEventMAPIT();
    }

    const matchedProduct = products.find((product: { sku: string | undefined }) => product.sku === productSku);

    if (!matchedProduct) {
      return EventHelper.isEventMAPIT();
    }

    return isProductWithTax(matchedProduct);
  };

  const persistReceipt = async (printReceiptParams: PrintReceipt, providedBlob?: Blob, providedOrderId?: string) => {
    const { badgeId, orderId } = printReceiptParams;
    const gbsCode = EventHelper.eventEdition?.gbsCode ?? "";
    let receiptBlob: Blob;
    let blobOrderId: string | undefined;

    try {
      if (providedBlob) {
        receiptBlob = providedBlob;
      } else {
        const generatedReceipt = await getReceiptBlob({
          ...printReceiptParams,
          isInclusiveTax: getInclusiveTax(printReceiptParams)
        });
        receiptBlob = generatedReceipt.blob;
        blobOrderId = generatedReceipt.orderId;
      }
    } catch (e) {
      console.error("Failed to build receipt blob for persistence", e);
      return;
    }

    const resolvedOrderId =
      orderId || providedOrderId || blobOrderId || printReceiptParams.badge?.orderId || printReceiptParams.orders?.[0]?.orderId;

    if (!printReceiptParams.orders && !resolvedOrderId) {
      console.error("Unable to persist receipt: no orderId could be resolved");
      return;
    }

    const fileNameOrderId = resolvedOrderId || "unknown";
    const file = new File([receiptBlob], `receipt-${fileNameOrderId}.pdf`, { type: "application/pdf" });

    try {
      if (printReceiptParams.orders) {
        for (const order of printReceiptParams.orders) {
          await PublicApiService.saveReceipt(file, gbsCode, order.orderId, order.badgeId);
        }
      } else {
        await PublicApiService.saveReceipt(file, gbsCode, resolvedOrderId as string, badgeId);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const printReceipt = async (printReceiptParams: PrintReceipt) => {
    const cachedOptions = PrintSettingsCache.get();
    const { blob: receiptBlob, orderId: receiptOrderId } = await getReceiptBlob({
      ...printReceiptParams,
      isInclusiveTax: getInclusiveTax(printReceiptParams)
    });

    if (cachedOptions.printMethod === PrintMethods.PRINT_DIRECT && eventEditionDetails?.businessUnitGbsCode !== BusinessUnit.AUSTRALIA) {
      directReceiptPrint(receiptBlob);
    } else {
      await printWithServerWithOptions(receiptBlob);
    }

    await persistReceipt(printReceiptParams, receiptBlob, receiptOrderId);
  };

  const fetchBlobFromUrl = async (downloadUrl: string): Promise<Blob> => {
    const response = await fetch(downloadUrl);
    return await response.blob();
  };

  const printDirectlyFromUrl = async (downloadUrl: string) => {
    try {
      const blob = await fetchBlobFromUrl(downloadUrl);
      const blobUrl = URL.createObjectURL(blob);
      print({
        printable: blobUrl,
        type: "pdf",
        showModal: false
      });

      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Failed to print receipt:", err);
    }
  };

  const printReceiptFromDownloadUrl = async (downloadUrl: string) => {
    const cachedOptions = PrintSettingsCache.get();
    const blob = await fetchBlobFromUrl(downloadUrl);

    if (cachedOptions.printMethod === PrintMethods.PRINT_DIRECT && eventEditionDetails?.businessUnitGbsCode !== BusinessUnit.AUSTRALIA) {
      await printDirectlyFromUrl(downloadUrl);
    } else {
      await printWithServerWithOptions(blob);
    }
  };

  return {
    printReceipt,
    printWithServer,
    fetchBlobFromUrl,
    printDirectlyFromUrl,
    getReceiptBlob,
    persistReceipt,
    printWithServerWithOptions,
    directReceiptPrint,
    printReceiptFromDownloadUrl
  };
};

export default useReceiptPrint;
